/**
 * HowToGetIntoFirm — `/how-to-get-into/:slug`.
 *
 * SEO wedge for the long-tail "how to get into {Firm}" query. Answers
 * the question a driven family types into Google late at night. Uses
 * the same feeder-unis endpoint that powers EmployerCompare, then
 * layers on: an editorial primer (offer rate, timeline, target
 * courses), the top-N feeder universities table with sourced cohort
 * %s, adjacent-sector firm suggestions, and links straight into the
 * Advanced Search for the intended subject so the reader can convert
 * from "I read a guide" to "I built a shortlist".
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Briefcase, Trophy, Clock, Target,
  ExternalLink, BookOpen, Landmark, Sparkles, ShieldCheck,
  TrendingUp, PoundSterling,
} from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";
import EventCard from "@/components/app/EventCard";
import FirmLogo from "@/components/app/FirmLogo";
import { trackEvent } from "@/lib/analytics";

// Same band vocabulary as EmployerFlow — keeps one consistent
// language for "strength of pipeline" across every widget on the
// site. Precise cohort % is stored server-side and used to compute
// the band but never displayed as a decimal (we've flipped this UX
// site-wide because public data doesn't support that precision).
const BAND_META = {
  strong:   { label: "Strong pipeline",   bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  moderate: { label: "Regular hires",      bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-300" },
  emerging: { label: "Emerging pipeline", bg: "bg-stone-100",   text: "text-stone-700",   border: "border-stone-300" },
  trace:    { label: "Occasional",         bg: "bg-stone-50",    text: "text-stone-500",   border: "border-stone-200" },
};

// Sector-level context that gives the page its opening editorial
// weight. Keeps the guide from reading like a wiki dump — the
// reader should feel a real editor drafted this. Falls back to
// "Global employer" copy when the sector isn't in the map.
const SECTOR_PRIMERS = {
  banking: {
    label: "Investment banking",
    icon: Landmark,
    typical_route: "Spring Week → Summer Internship → Full-Time Analyst",
    early_action: "Apply to Spring Weeks in Year 12 / Year 1 of uni. Most banks close applications by early November.",
    academic_bar: "Target: A*AA/AAA at A-Level; 40+ IB; predicted 2:1 minimum, 1st preferred at target unis.",
    hard_signals: "Numerate degree (Economics, Maths, Physics, Engineering) helps but non-quant Humanities from Oxbridge/LSE also land seats.",
  },
  consulting: {
    label: "Management consulting",
    icon: Target,
    typical_route: "Insight Day → Off-Cycle Internship → Summer Associate → Full-Time",
    early_action: "Case-prep starts 12-18 months before applications. Apply mid-July for the following autumn's intakes.",
    academic_bar: "MBB (McKinsey / BCG / Bain) filter aggressively on brand + 1st or high 2:1. Big-4 strategy arms are more open.",
    hard_signals: "Strong extracurricular leadership + numeracy on the CV. Cases are the equaliser once you clear CV screen.",
  },
  law: {
    label: "Corporate law",
    icon: BookOpen,
    typical_route: "First-Year Insight → Vacation Scheme → Training Contract",
    early_action: "Vac scheme applications open the summer before Year 2 of uni. Apply the moment portals open — most fill on rolling basis.",
    academic_bar: "AAA at A-Level, 1st or high 2:1 preferred at target Magic Circle firms. Some now accept 2:1 with strong CV.",
    hard_signals: "Non-law undergrads take the SQE prep post-degree — city firms sponsor it and prefer diverse degree backgrounds.",
  },
  tech: {
    label: "Software engineering & AI",
    icon: Sparkles,
    typical_route: "Off-Cycle Internship → Summer Internship → Full-Time SDE",
    early_action: "FAANG summer intern apps open August/September for the following summer. Grind LeetCode 8-12 weeks pre-interview.",
    academic_bar: "Applications rely more on GitHub + LeetCode signal than raw academics — but a 2:1+ from a CS-heavy uni still filters.",
    hard_signals: "Contribute to one shipped side-project. Half the offers come from side-project referrals, not the cold pipeline.",
  },
  accounting: {
    label: "Big 4 accounting",
    icon: Briefcase,
    typical_route: "Insight Day → Summer Vacation Programme → Full-Time Graduate",
    early_action: "Apply mid-August to mid-October the year before start. Most schemes close by November on a rolling basis.",
    academic_bar: "300 UCAS points / 2:1 is the published bar; competition for London-office seats runs higher in practice.",
    hard_signals: "Big 4 fund the ACA — arguably the best combined training + salary trade in graduate finance.",
  },
};

function primerFor(sector) {
  const key = (sector || "").toLowerCase();
  return SECTOR_PRIMERS[key] || null;
}

export default function HowToGetIntoFirm() {
  const { slug } = useParams();
  const [firm, setFirm] = useState(null);
  const [feeder, setFeeder] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(null);
  // Active subject filter. Empty string = "All". When the user picks
  // a chip we re-fetch feeder-unis with ?subject= so the mini-flow +
  // main table + top-5 chips update in lockstep.
  const [subject, setSubject] = useState("");
  // Country filter for the deep feeder table — "" = GLOBAL, or a
  // country name from raw_country_counts, or the sentinel "__rest__"
  // for anything outside the top-2 explicit tabs.
  const [countryTab, setCountryTab] = useState("");
  const [claimState, setClaimState] = useState(null);
  // Guard against StrictMode double-mount inflating page-view analytics.
  const _trackedSlug = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setFirm(null); setFeeder(null); setErr(null);

    // Bulk-fetch the three shapes we need in parallel. Feeder is the
    // canonical source of firm_name because it always resolves (even
    // for pure alias slugs). Also pull the public claim-status so we
    // know if a firm has been claimed (renders the Verified Partner
    // chip) and pick up any firm-submitted blurb / logo / CTA.
    // When a country tab is picked we deliberately switch to a
    // country-scoped fetch with a much higher limit — otherwise the
    // deep-table would only surface whichever rows from that country
    // happened to survive the global top-25 slice (e.g. "US (317)"
    // rendering as one row was a real 2026-02 bug report).
    const activeLimit = countryTab ? 200 : 25;
    const feederUrl = `/employers/${encodeURIComponent(slug)}/feeder-unis?limit=${activeLimit}${subject ? `&subject=${encodeURIComponent(subject)}` : ""}${countryTab ? `&country=${encodeURIComponent(countryTab)}` : ""}`;
    Promise.all([
      api.get(feederUrl),
      api.get(`/employers/curated`),
      api.get(`/firm-claims/status/${encodeURIComponent(slug)}`).catch(() => ({ data: null })),
    ])
      .then(([f, curated, claimStatus]) => {
        if (cancelled) return;
        setFeeder(f.data);
        setClaimState(claimStatus?.data || null);
        // Track a firm-page view for the Firm Portal Analytics tab.
        // Referrer subject is passed through from a degree-page firm
        // chip if present in the URL query (?ref_subject=Economics).
        // Guard with a ref so StrictMode double-mount doesn't inflate counts.
        if (_trackedSlug.current !== slug) {
          _trackedSlug.current = slug;
          try {
            const url = new URL(window.location.href);
            const refSubject = url.searchParams.get("ref_subject") || null;
            const refDegreeId = url.searchParams.get("ref_degree_id") || null;
            trackEvent("firm_page_view", {
              firm_slug: slug,
              ...(refSubject ? { referrer_subject: refSubject } : {}),
              ...(refDegreeId ? { referrer_degree_id: refDegreeId } : {}),
            });
          } catch (_e) { /* noop */ }
        }
        const items = curated.data.items || [];
        const self = items.find((i) => i.slug === slug);
        setFirm(self || null);
        // Related = same sector, exclude self, up to 6.
        const sector = self?.sector || "";
        setRelated(
          items
            .filter((i) => i.slug !== slug && (i.sector || "") === sector)
            .slice(0, 6)
        );
      })
      .catch(() => !cancelled && setErr("network"));
    return () => { cancelled = true; };
  }, [slug, subject, countryTab]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-stone-500">Couldn't load this employer guide.</p>
      </div>
    );
  }
  if (!feeder) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="h-6 w-40 bg-stone-100 rounded animate-pulse mb-4" />
        <div className="h-10 w-96 bg-stone-100 rounded animate-pulse mb-6" />
        <div className="h-4 w-full bg-stone-100 rounded animate-pulse mb-3" />
        <div className="h-4 w-3/4 bg-stone-100 rounded animate-pulse" />
      </div>
    );
  }

  const firmName = feeder.firm_name || (firm?.firm_name) || slug.replace(/-/g, " ");
  const feederRows = feeder.feeder_unis || [];
  const primer = primerFor(firm?.sector);
  const PrimerIcon = primer?.icon || Briefcase;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="firm-guide-page">
      <PathwaySEO
        title={`How to get into ${firmName} — the universities that actually feed graduates there`}
        description={`Sourced from graduate destinations data: which universities send the highest % of cohorts to ${firmName}, the typical academic bar, application timeline, and the internship route that leads to a full-time offer.`}
        path={`/how-to-get-into/${slug}`}
      />

      <Link
        to="/employers"
        data-testid="firm-guide-back"
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
      >
        <ArrowLeft className="h-3 w-3" /> All employers
      </Link>

      {/* HERO */}
      <div className="mt-6">
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Trophy className="h-3 w-3" /> Firm guide
        </div>
        <div className="mt-2 flex items-start gap-4">
          <FirmLogo
            slug={slug}
            name={firmName}
            sector={feeder.sector || firm?.sector}
            IconFallback={Briefcase}
            size={64}
          />
          <h1
            data-testid="firm-guide-heading"
            className="font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
          >
            How to get into {firmName}
          </h1>
        </div>

        {/* Firm-submitted blurb (editor-approved) — shown INSTEAD of the
            default editorial intro when the firm has published one. Chip
            makes the provenance explicit. */}
        {claimState?.firm_submitted?.blurb ? (
          <div className="mt-4 max-w-3xl" data-testid="firm-guide-firm-submitted-blurb">
            <div className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border border-emerald-400 bg-emerald-50 text-emerald-900 px-2 py-0.5">
              <ShieldCheck className="h-2.5 w-2.5" /> Firm-submitted · editor-approved
            </div>
            <p className="mt-3 text-stone-700 leading-relaxed">
              {claimState.firm_submitted.blurb}
            </p>
          </div>
        ) : (
          <p className="mt-3 text-stone-700 leading-relaxed max-w-3xl">
            The universities on this page are the ones whose graduate
            destinations data actually names {firmName} — no ranking rumour,
            no "prestige score", just where their graduates end up
            according to the sources linked below.
          </p>
        )}

        {/* Small badge row for verified-partner + Claim CTA */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {claimState?.tier === "verified_partner" && (
            <span
              data-testid="firm-guide-verified-partner-chip"
              className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider rounded-full border border-emerald-400 bg-emerald-50 text-emerald-900 px-2 py-0.5"
            >
              <ShieldCheck className="h-2.5 w-2.5" /> Verified Partner
            </span>
          )}
          {claimState && !claimState.is_claimed && (
            <Link
              to={`/claim/${slug}`}
              data-testid="firm-guide-claim-cta"
              className="text-[11px] text-[#4C1D95] hover:text-[#3B0764] font-medium hover:underline inline-flex items-center gap-1"
            >
              Is this your firm? Claim this profile →
            </Link>
          )}
          {claimState?.firm_submitted?.cta_url && (
            <a
              href={claimState.firm_submitted.cta_url}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("firm_cta_click", { firm_slug: slug })}
              data-testid="firm-guide-firm-cta-url"
              className="text-[11px] text-[#4C1D95] hover:text-[#3B0764] font-medium inline-flex items-center gap-1"
            >
              Apply on {firmName}'s careers page <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>

        {/* Prominent salary-bands CTA — sits directly under the hero
            so families see the firm's published pay before scrolling.
            Green-outlined pill mirrors the "career-accelerator"
            language on the /employers/{slug} page. */}
        <Link
          to={`/employers/${slug}`}
          data-testid="firm-guide-salary-bands-link"
          onClick={() => trackEvent("firm_salary_bands_click", { firm_slug: slug })}
          className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-emerald-500 bg-emerald-50 hover:bg-emerald-500 hover:text-white text-emerald-800 text-sm font-semibold px-4 py-2 transition-colors group"
        >
          <PoundSterling className="h-4 w-4" />
          See {firmName}'s salary bands
          <span className="font-mono text-[10px] uppercase tracking-widest opacity-70 group-hover:opacity-100">
            firm-published
          </span>
          <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* MINI FEEDER FLOW — top 5 unis at a glance, above the fold */}
      {feederRows.length > 0 && (
        <section
          data-testid="firm-guide-mini-flow"
          className="mt-8 rounded-lg border border-stone-200 bg-white p-5"
        >
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#4C1D95]" />
            <div className="label-uppercase text-[#4C1D95]">Top feeder unis at a glance</div>
          </div>
          <p className="mt-1.5 text-[12px] font-underground-sc tracking-wider text-stone-500">
            The 5 strongest {firmName} pipelines we've sourced
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {feederRows.slice(0, 5).map((r, i) => {
              const band = BAND_META[r.strength_band] || BAND_META.emerging;
              return (
                <Link
                  key={`mini-${r.university_id}-${r.subject_area}-${i}`}
                  to={`/uni/${r.university_id}`}
                  data-testid={`firm-guide-mini-flow-chip-${i}`}
                  className={`inline-flex items-center gap-2 rounded-md border ${band.border} ${band.bg} px-3 py-1.5 hover:shadow-sm transition-shadow`}
                >
                  <span className="font-mono text-[11px] text-stone-500 tabular-nums">
                    #{i + 1}
                  </span>
                  <span className="font-medium text-sm text-[#002147]">
                    {r.university_name || r.university_id}
                  </span>
                  <span className="text-[11px] font-mono text-stone-500">
                    · {r.subject_area}
                  </span>
                  <span className={`text-[9px] font-underground-sc tracking-wider ${band.text}`}>
                    {band.label}
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-3 text-[11px] font-underground-sc tracking-wider text-stone-500">
            Scroll down for the full ranked feeder table + entry-route primer
          </div>
        </section>
      )}

      {/* SECTOR PRIMER */}
      {primer && (
        <section
          data-testid="firm-guide-primer"
          className="mt-8 rounded-lg border border-[#4C1D95]/30 bg-[#4C1D95]/[0.03] p-5"
        >
          <div className="flex items-center gap-2 text-[#4C1D95]">
            <PrimerIcon className="h-4 w-4" />
            <div className="label-uppercase text-[#4C1D95]">{primer.label} · the route</div>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4 text-[13px] text-stone-700 leading-relaxed">
            <PrimerRow icon={ArrowRight} title="Typical pipeline" body={primer.typical_route} />
            <PrimerRow icon={Clock} title="When to apply" body={primer.early_action} />
            <PrimerRow icon={BookOpen} title="Academic bar" body={primer.academic_bar} />
            <PrimerRow icon={Target} title="The real filter" body={primer.hard_signals} />
          </div>
        </section>
      )}

      {/* Upcoming events strip — approved firm-published events surfaced
          inline so students see them right after the primer. */}
      <FirmEventsStrip firmSlug={slug} firmName={firmName} />

      {/* FEEDER UNI TABLE */}
      <section className="mt-10" data-testid="firm-guide-feeder-section">
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Landmark className="h-3 w-3" /> Feeder universities
        </div>
        <h2 className="mt-2 font-editorial text-2xl sm:text-3xl leading-tight text-[#002147]">
          Where {firmName}'s {subject ? `${subject.toLowerCase()} ` : ""}graduates actually came from
        </h2>

        {/* Subject chip row — same UX as EmployerFlow's chip row so
            students can slice "Goldman's top feeders for Economics"
            vs "for CS". Only rendered when there's more than one
            subject to choose from. */}
        {(feeder.available_subjects || []).length > 1 && (
          <div
            data-testid="firm-guide-subject-chips"
            className="mt-4 flex flex-wrap gap-1.5"
          >
            <button
              type="button"
              onClick={() => setSubject("")}
              data-testid="firm-guide-subject-chip-all"
              className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                subject === ""
                  ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                  : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]"
              }`}
            >
              All ({(feeder.available_subjects || []).reduce((n, s) => n + s.degrees_count, 0)})
            </button>
            {(feeder.available_subjects || []).map((s) => {
              const active = subject === s.subject;
              return (
                <button
                  type="button"
                  key={s.subject}
                  data-testid={`firm-guide-subject-chip-${s.subject}`}
                  onClick={() => setSubject(active ? "" : s.subject)}
                  className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]"
                  }`}
                >
                  {s.subject} ({s.degrees_count})
                </button>
              );
            })}
          </div>
        )}

        {/* Country tabs — GLOBAL · <primary> · <secondary> · REST.
            Materialised from feeder.country_diversity.raw_country_counts
            so a firm with hires from only 1-2 countries won't get an
            over-detailed tab set.  Filter is client-side on feederRows
            since the response is already country-diversified upstream. */}
        {(() => {
          const rawCounts = feeder?.country_diversity?.raw_country_counts || {};
          const rawUniCounts = feeder?.country_diversity?.raw_country_unique_unis || {};
          const countries = Object.entries(rawCounts)
            .filter(([c]) => c && c !== "?")
            .sort((a, b) => b[1] - a[1]);
          if (countries.length < 2) return null;
          const top2 = countries.slice(0, 2).map(([c]) => c);
          const restCount = countries.slice(2).reduce((n, [, v]) => n + v, 0);
          // Unique-uni totals for Global + Rest of world (unions of
          // countries in scope). Row counts double-count course ×
          // uni pairs; families want to see "300 unis" not "317".
          const uniquePairs = new Set();
          // Only the pre-filter response's `feeder_unis` gives us the
          // uni_id per row; but rawUniCounts already summarises per
          // country. For Global/Rest we sum the per-country sets —
          // this over-counts multi-country brand groups (e.g. NYU +
          // NYU Abu Dhabi), which is acceptable here.
          const globalUnis = Object.entries(rawUniCounts).reduce((n, [, v]) => n + v, 0);
          const restUnis = Object.entries(rawUniCounts)
            .filter(([c]) => !top2.includes(c))
            .reduce((n, [, v]) => n + v, 0);
          return (
            <div
              data-testid="firm-guide-country-tabs"
              className="mt-4 flex flex-wrap gap-1.5 items-center border-b border-stone-200 pb-3"
            >
              <span className="text-[10px] font-underground-sc tracking-wider text-stone-500 mr-1">
                Country lens
              </span>
              {[
                {
                  key: "",
                  label: "Global",
                  unis: globalUnis || null,
                  rows: countries.reduce((n, [, v]) => n + v, 0),
                },
                ...top2.map((c) => ({
                  key: c,
                  label: c,
                  unis: rawUniCounts[c] || null,
                  rows: rawCounts[c],
                })),
                ...(restCount > 0
                  ? [{ key: "__rest__", label: "Rest of world", unis: restUnis || null, rows: restCount }]
                  : []),
              ].map(({ key, label, unis, rows }) => {
                const active = countryTab === key;
                return (
                  <button
                    key={key || "global"}
                    type="button"
                    onClick={() => setCountryTab(active && key !== "" ? "" : key)}
                    data-testid={`firm-guide-country-tab-${key || "global"}`}
                    title={`${label}: ${unis ? `${unis.toLocaleString()} universities` : ""}${unis && rows ? " · " : ""}${rows ? `${rows.toLocaleString()} feeder rows` : ""}`}
                    className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                      active
                        ? "bg-[#002147] text-white border-[#002147]"
                        : "bg-white text-stone-700 border-stone-300 hover:border-[#002147]"
                    }`}
                  >
                    {label}
                    {unis ? (
                      <span className="ml-1.5 opacity-80">
                        {unis.toLocaleString()} uni{unis === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span className="ml-1.5 opacity-80">({rows})</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}

        {feederRows.length === 0 ? (
          <div
            data-testid="firm-guide-no-feeder"
            className="mt-4 rounded-md border border-amber-300 bg-amber-50/60 p-4 text-[13px] text-stone-700"
          >
            We don't yet have {firmName} named in any graduate destinations survey we've
            processed. This usually happens when the firm hires under a subsidiary label
            (e.g. "GS Group" for Goldman Sachs) or hires primarily internationals we
            haven't ingested yet. Try one of the related firms below, or search Advanced
            Match by dream employer.
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-stone-200 overflow-x-auto">
            {feeder.any_editorial_pattern && (
              <div
                data-testid="firm-guide-feeder-disclaimer"
                className="border-b border-amber-300 bg-amber-50/60 px-3 py-2 text-[12px] text-stone-700"
              >
                Rows carry a <span className="font-underground-sc tracking-wider text-amber-800">PATTERN</span>{" "}
                chip where the destination data is drawn from public careers
                reports + firm target-school lists rather than audited HESA
                data. Bands (<span className="font-underground-sc tracking-wider text-emerald-800">strong</span> ·{" "}
                <span className="font-underground-sc tracking-wider text-amber-800">regular</span> ·{" "}
                <span className="font-underground-sc tracking-wider text-stone-600">emerging</span>) reflect
                signal strength; we don't publish precise cohort %s.{" "}
                <Link to="/methodology" className="underline text-[#4C1D95]">Methodology</Link>.
              </div>
            )}
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-left border-b border-stone-200 bg-stone-50 font-underground-sc text-[10px] text-stone-500 tracking-wider">
                  <th className="py-2.5 px-3">#</th>
                  <th className="py-2.5 px-3">University</th>
                  <th className="py-2.5 px-3">Subject</th>
                  <th className="py-2.5 px-3">Signal</th>
                  <th className="py-2.5 px-3 hidden sm:table-cell">Survey</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Client-side country filter. Backend already returned
                  // the diversified top-N; here we just gate the visible
                  // rows based on the active tab. "" = show all, a
                  // country name = only that country, "__rest__" =
                  // anything NOT in the top-2 country tabs.
                  const rawCounts = feeder?.country_diversity?.raw_country_counts || {};
                  const topTwo = new Set(
                    Object.entries(rawCounts)
                      .filter(([c]) => c && c !== "?")
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 2)
                      .map(([c]) => c)
                  );
                  const filtered = feederRows.filter((r) => {
                    if (!countryTab) return true;
                    if (countryTab === "__rest__") return r.country && !topTwo.has(r.country);
                    return r.country === countryTab;
                  });
                  if (filtered.length === 0) {
                    return (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-4 px-3 text-center text-[12px] text-stone-500"
                          data-testid="firm-guide-feeder-empty-country"
                        >
                          No {firmName} feeders in {countryTab === "__rest__" ? "the rest of the world" : countryTab} in our current dataset. Try Global.
                        </td>
                      </tr>
                    );
                  }
                  return filtered.map((r, i) => {
                    const band = BAND_META[r.strength_band] || BAND_META.emerging;
                    return (
                      <tr
                        key={`${r.university_id}-${r.subject_area}-${i}`}
                        data-testid={`firm-guide-feeder-row-${i}`}
                        className="border-b border-stone-100 hover:bg-stone-50/60"
                      >
                        <td className="py-2.5 px-3 font-mono text-stone-500">{i + 1}</td>
                        <td className="py-2.5 px-3">
                          <Link
                            to={`/uni/${r.university_id}`}
                            className="text-[#4C1D95] hover:underline font-medium"
                          >
                            {r.university_name || r.university_id}
                          </Link>
                          {r.country && (
                            <span className="ml-2 text-[11px] font-mono text-stone-500">
                              · {r.country}
                            </span>
                          )}
                          {r.is_editorial_pattern && (
                            <span
                              data-testid={`firm-guide-feeder-row-${i}-pattern`}
                              title="Pattern-based — sourced from published careers reports + firm target-school lists, not audited HESA data."
                              className="ml-2 inline-block text-[9px] font-underground-sc tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-[1px] align-middle"
                            >
                              Pattern
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-stone-700">{r.subject_area || "—"}</td>
                        <td className="py-2.5 px-3">
                          <span
                            data-testid={`firm-guide-feeder-row-${i}-band`}
                            className={`inline-block text-[10px] font-underground-sc tracking-wider ${band.bg} ${band.text} border ${band.border} rounded-full px-2 py-[2px]`}
                          >
                            {band.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 hidden sm:table-cell text-[11px] font-mono text-stone-500">
                          {r.survey_year || r.source || "—"}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
        {feederRows.length > 0 && (
          <p className="mt-3 text-[11px] font-mono text-stone-500">
            Ranked by pipeline strength. Definitions:{" "}
            <Link to="/methodology" className="underline text-[#4C1D95]">
              how we source graduate destinations
            </Link>
            .
          </p>
        )}
      </section>

      {/* CTA to Advanced Search */}
      <section className="mt-10 rounded-lg border border-emerald-300 bg-emerald-50/40 p-5">
        <div className="flex items-center gap-2 text-emerald-800">
          <Sparkles className="h-4 w-4" />
          <div className="label-uppercase">Turn this into a shortlist</div>
        </div>
        <p className="mt-2 text-[14px] text-stone-700 leading-relaxed">
          Feed the feeder table into Advanced Search and we'll rank
          every degree in your existing profile's country + subject
          scope on six layers — with the career layer <em>cranked
          up</em> to 32% (its normal weight is 15%) so pipeline unis
          genuinely dominate the ranking, not just get a light nudge.
          Your grades, budget, visa and personal fit still apply as
          normal — this doesn't restrict the search, it re-prioritises
          it. Once you're on the results page you can also flip a
          "Show only pipeline unis" toggle for a hard filter.
        </p>
        <Link
          to={`/advanced?dream_employer=${encodeURIComponent(firmName)}&boost_career=1`}
          data-testid="firm-guide-cta-advanced"
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#4C1D95] px-4 py-2 text-white text-sm font-medium hover:bg-[#3B0764]"
        >
          Rank my matches with {firmName} pipeline boosted <ArrowRight className="h-4 w-4" />
        </Link>
        <div className="mt-3">
          <Link
            to={`/get-me-to/${slug}`}
            data-testid="firm-guide-cta-get-me-to"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#4C1D95] hover:text-[#3B0764] font-medium"
          >
            Or take the 3-question quick path — grades + budget + country → filtered shortlist
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* RELATED FIRMS */}
      {related.length > 0 && (
        <section className="mt-10">
          <div className="label-uppercase text-stone-600">Related firms in {firm?.sector || "the same sector"}</div>
          <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/how-to-get-into/${r.slug}`}
                data-testid={`firm-guide-related-${r.slug}`}
                className="rounded-md border border-stone-200 bg-white p-3 hover:border-[#4C1D95] hover:bg-[#4C1D95]/[0.03] transition"
              >
                <div className="font-medium text-[#002147] leading-snug">{r.firm_name}</div>
                <div className="mt-1 text-[11px] font-mono text-stone-500">
                  {(r.countries || []).slice(0, 3).join(" · ") || r.sector}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer nav */}
      <div className="mt-12 text-[13px] text-stone-500 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>Also see:</span>
        <Link to={`/employers/${slug}`} className="text-[#4C1D95] underline inline-flex items-center gap-1">
          {firmName} salary bands <ExternalLink className="h-3 w-3" />
        </Link>
        <span>·</span>
        <Link to="/employers" className="text-[#4C1D95] underline">
          all firm guides
        </Link>
        <span>·</span>
        <Link to="/methodology" className="text-[#4C1D95] underline">
          data methodology
        </Link>
      </div>
    </div>
  );
}

function PrimerRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 mt-0.5 text-[#4C1D95] shrink-0" />
      <div>
        <div className="label-uppercase !text-[10px]">{title}</div>
        <div className="mt-0.5 text-stone-700">{body}</div>
      </div>
    </div>
  );
}


// -------------------- Firm-page events strip --------------------
function FirmEventsStrip({ firmSlug, firmName }) {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!firmSlug) return;
    api
      .get(`/events?firm_slug=${encodeURIComponent(firmSlug)}&limit=6`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoaded(true));
  }, [firmSlug]);
  if (!loaded || events.length === 0) return null;
  return (
    <section className="mt-10" data-testid="firm-guide-events-strip">
      <div className="label-uppercase text-[#4C1D95]">Upcoming from {firmName}</div>
      <h2 className="mt-2 font-editorial text-2xl sm:text-3xl leading-tight text-[#002147]">
        Events open to applicants right now
      </h2>
      <p className="mt-2 text-sm text-stone-600 max-w-2xl">
        Officially published by {firmName} and reviewed by our editorial team.
        RSVP links go directly to the firm's application portal.
      </p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="firm-guide-events-list">
        {events.map((e) => (
          <EventCard key={e.id} event={e} showFirm={false} />
        ))}
      </div>
    </section>
  );
}
